import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { BloodDrip } from "@/components/BloodDrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopTeaser() {
  const [products, setProducts] = useState([]);
  const [storeOpen, setStoreOpen] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data.slice(0, 4)))
      .catch(() => {});
    axios
      .get(`${API}/store-status`)
      .then((r) => setStoreOpen(r.data.open))
      .catch(() => {});
  }, []);

  return (
    <section id="shop" data-testid="shop-teaser" className="relative py-28 sm:py-36 newsprint-grain">
      <div className="absolute inset-0 blood-vignette pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block"
        >
          a taste of the rack
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-14"
        >
          THE <span className="text-crimson">CRYPT SHOP</span>
          <BloodDrip />
        </motion.h2>

        {!storeOpen ? (
          <div data-testid="teaser-store-closed" className="border-2 border-blooddeep bg-pulp p-10 text-center max-w-xl mx-auto mb-14">
            <p className="font-display text-xl sm:text-2xl uppercase text-paper mb-3">
              UNDER <span className="text-crimson">CONSTRUCTION</span>
            </p>
            <p className="font-type text-sm text-paper/70">
              Store is currently under construction, please check back later.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-14">
          {products.map((p, i) => (
            <motion.a
              key={p.id}
              href="/shop"
              data-testid={`teaser-product-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group text-left"
            >
              <div className="spotlight-frame aspect-[2/3] mb-4">
                <img
                  src={p.cover}
                  alt={`${p.title} cover`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <span className="absolute top-3 -left-1 bg-blooddeep text-white font-marker text-[11px] px-2.5 py-0.5 -rotate-3 ink-shadow-sm">
                  {p.badge}
                </span>
                {p.was_price && p.was_price > p.price && (
                  <span
                    data-testid={`sale-tag-${p.id}`}
                    className="absolute top-9 -left-1 bg-crimson text-white font-marker text-[11px] px-2.5 py-0.5 rotate-2 ink-shadow-sm"
                  >
                    ON SALE
                  </span>
                )}
              </div>
              <h3 className="font-impact uppercase tracking-wide text-base sm:text-lg text-paper transition-colors duration-150 group-hover:text-crimson">
                {p.title}
              </h3>
              <p className="mt-1 -rotate-1 inline-flex items-baseline gap-2">
                {p.was_price && p.was_price > p.price && (
                  <span className="font-courier text-xs text-paper/40 line-through">${p.was_price.toFixed(2)}</span>
                )}
                <span className="font-marker text-bloodglow text-sm">${p.price.toFixed(2)}</span>
              </p>
            </motion.a>
          ))}
        </div>
        )}

        {storeOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center"
        >
          <a
            href="/shop"
            data-testid="shop-full-inventory-button"
            className="inline-flex items-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-white border-2 border-black px-8 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1"
          >
            <Flame size={18} /> Shop Full Inventory
          </a>
        </motion.div>
        )}
      </div>
    </section>
  );
}
